import msConfigTemplate from "./ms-config-template";
import { E_PaymentGate } from "../models/data-model";

/**
 * @typedef PWAFileSystemHandler
 * @type {{
 *     action: string,
 *     accept: Object<string, string[]>
 * }}
 * @see https://developer.mozilla.org/en-US/docs/Web/Manifest/file_handlers
 */

/**
 * @typedef PWAIcon
 * @type {{
 *  src: string,
 *  sizes: string|"any",
 *  type: string,
 * }}
 */

/**
 * @typedef PWARelatedApplication
 * @type {{
 *  platform: "play"|"itunes"|"windows"|string,
 *  url: string,
 *  id: string,
 * }}
 * @see https://developer.mozilla.org/en-US/docs/Web/Manifest/related_applications
 */

/**
 * @typedef PWAShotcut
 * @type {{
 *  name: string,
 *  short_name: string,
 *  description: string,
 *  url: string,
 *  icons: [PWAIcon]
 * }}
 */

/**
 * @typedef Manifest
 * @type {{
 *  background_color: string,
 *  categories: string[],
 *  description: string,
 *  display: "browser"|"fullscreen"|"standalone"|"minimal-ui",
 *  display_override: "browser"|"fullscreen"|"standalone"|"minimal-ui"|"window-controls-overlay",
 *  file_handlers: PWAFileSystemHandler[],
 *  icons: PWAIcon[],
 *  id: number,
 *  launch_handler: {
 *      client_mode: "auto"|"focus-existing"|"navigate-existing"|"navigate-new"|["auto"|"focus-existing"|"navigate-existing"|"navigate-new"]
 *  },
 *  name: string,
 *  orientation: "any"|"natural"|"landscape"|"landscape-primary"|"landscape-secondary"|"portrait"|"portrait-primary"|"portrait-secondary",
 *  prefer_related_applications: boolean,
 *  protocol_handlers: [{protocol: string, url: string}],
 *  related_applications: PWARelatedApplication[],
 *  scope: string,
 *  screenshots: [{src: string, sizes: string, type: string, platform: "wide"|"narrow", label: string}],
 *  share_target: {action: string, method: "GET"|"POST", params: {title: string, text: string, url: string, files: Object<string, string|[string]>}},
 *  short_name: string,
 *  shortcuts: PWAShotcut[],
 *  start_url: string,
 *  themes: string[],
 * }}
 * @see https://developer.mozilla.org/en-US/docs/Web/Manifest
 *
 * Note: `themes` is ours, not part of the web app manifest spec - it lists the [ThemeBase]s the
 * environment offers the user, the first one being its default (see src/themes). `theme_color`
 * and `background_color` are deliberately absent: they come from the active theme and are
 * injected when the manifest is serialized.
 */

export class EnvBase {
	/** @type {Manifest} **/
	static manifest;
	/** @type {Manifest} **/
	manifest = this.constructor.manifest;
	dataFromJson = undefined;

	/**
	 * Is applied
	 * ---
	 * Returns whether this environment (Only this; no inheritors) is currently active and thus applied
	 * @returns {boolean};
	 */
	static isApplied() {
		return window.activeEnvironment.is(this);
	}

	static isAppliedOrAnyDescendant() {
		return window.activeEnvironment.isOf(this);
	}

	/**
	 * @abstract
	 * Environment ID
	 * ---
	 * Returns a unique identifier for this environment or throws an error if this method is not being overridden.
	 * @returns {string}
	 */
	static get environmentID() {
		if(this.manifest?.id) {
			return this.manifest.id;
		}

		throw Error(`Property "id" must be defined in the manifest.json! Or "static get environmentID()" must be overridden for class ${this.name}.`);
	};

	/**
	 * Environment name
	 * ---
	 * Returns a human-readable name fo the environment.
	 * @return {string}
	 */
	static get environmentName() {
		if(this.manifest?.name) {
			return this.manifest.name;
		}

		throw Error(`Property "name" must be defined in the manifest.json! Or "static get environmentName()" must be overridden for class ${this.name}.`);
	}

	/**
	 * Environment ID
	 * ---
	 * Returns a unique identifier for this environment or throws an error if this method is not being overridden.
	 * @return {string}
	 * @see EnvBase.environmentID
	 */
	get environmentID() {
		return this.constructor.environmentID;
	}

	/**
	 * Available themes
	 * ---
	 * The themes this environment offers the user, in the order they are listed in its manifest's
	 * `"themes"` property. The first one is the environment's default.
	 * @return {ThemeBase[]}
	 */
	get availableThemes() {
		return (this.manifest?.themes ?? []).map(themeID => {
			const theme = window.themes?.[themeID];

			if(!theme) {
				throw Error(`Unknown theme "${themeID}" offered by ${this.constructor.name}! Make sure every entry of the manifest's "themes" property names a theme registered in src/themes/themes.js.`);
			}

			return theme;
		});
	}

	/**
	 * Theme
	 * ---
	 * The theme this environment is currently wearing: the user's pick (see app.setBrandTheme),
	 * or the first entry of [availableThemes]. A pick this environment does not offer - left over
	 * from another environment - falls back to the default rather than being honoured.
	 *
	 * Everything visual - logo, palette, fonts, branded images - is delegated to it, so several
	 * environments of the same brand share one theme and rebranding one is a one-property edit.
	 * @return {ThemeBase}
	 */
	get theme() {
		const availableThemes = this.availableThemes;

		if(!availableThemes.length) {
			throw Error(`No themes offered by ${this.constructor.name}! The manifest's "themes" property must name at least one theme registered in src/themes/themes.js.`);
		}

		return availableThemes.find(theme => theme.themeID == localStorage.getItem("brandTheme")) ?? availableThemes[0];
	}

	/**
	 * @abstract
	 * Environment group
	 * ---
	 * Returns a group under which this environment is categorized.
	 * The return value is optional but highly recommended for ease of use during a development.
	 * @returns {string}
	 */
	get environmentGroup() {};

	/**
	 * Environment name
	 * ---
	 * A human readable name of the environment.
	 * Used in various places such as a title of the page.
	 * The value is retrieved from the provided manifest.
	 * @return {string}
	 */
	get environmentName() {
		return this.constructor.environmentName;
	};

	/**
	 * Assets environment
	 * ---
	 * Returns an environment class that is used to determine which assets (mostly icons for now) are used for this environment and it's descendants.
	 * Most of the time this will point to self. However, if there is a descendant, like a dev version of the same environment, this can be overridden for a simple asset substitution.
	 * @return {EnvBase}
	 */
	get assetsEnvironment() {
		return this;
	}

	/**
	 * Primary color
	 * ---
	 * @return {string}
	 * @see ThemeBase.primaryColor
	 */
	get primaryColor() {
		return this.theme.primaryColor;
	}

	/**
	 * Background color
	 * ---
	 * @return {string}
	 * @see ThemeBase.backgroundColor
	 */
	get backgroundColor() {
		return this.theme.backgroundColor;
	}

	/**
	 * @abstract
	 * API URLs
	 * ---
	 * Returns an URL for the API.
	 * @returns {string}
	 */
	get apiUrl() {
		return this.dataFromJson?.apiUrl;
	}

	/**
	 * Hostnames
	 * ---
	 * Hosts this environment is served from. `env-app-loader` matches `window.location.hostname`
	 * against these lists to pick the active environment, which is the only selection that works
	 * in a production build - the `localStorage.env` override is development-only, and the
	 * fallback is simply the first entry of envs.json.
	 *
	 * The list lives in envs.json rather than in the manifest so a tenant can be pointed at a
	 * different domain without a rebuild. An environment that declares none is never matched
	 * this way, which is how every environment behaved before the mechanism existed.
	 * @returns {string[]}
	 */
	get hostnames() {
		return this.dataFromJson?.hostnames ?? [];
	}

	/**
	 * @abstract
	 * Map configuration
	 * ---
	 * Returns an object containing various Google Maps related parameters.
	 * @returns {{
	 *  location: [longitude: number, latitude: number],
	 *  zoom: number,
	 * }}
	 */
	get mapConfiguration() {
		return this.dataFromJson?.mapConfiguration ?? {};
	}

	get allowCouponPurchases() {
		return this.dataFromJson?.allowCouponPurchases ?? true;
	}

	/**
	 * Logo for menu
	 * ---
	 * @return {string}
	 * @see ThemeBase.logoForMenu
	 */
	get logoForMenu() {
		return this.theme.logoForMenu;
	}

	/**
	 * Logo for loader
	 * ---
	 * @return {string}
	 * @see ThemeBase.logoForLoader
	 */
	get logoForLoader() {
		return this.theme.logoForLoader;
	}

	/**
	 * Style variables
	 * ---
	 * @return {Object<string, string>|undefined}
	 * @see ThemeBase.styleVariables
	 */
	get styleVariables() {
		return this.theme.styleVariables;
	}

	/**
	 * Style overrides
	 * ---
	 * @return {Object<string, HTMLTemplateElement>}
	 * @see ThemeBase.styleOverrides
	 */
	get styleOverrides() {
		return this.theme.styleOverrides;
	}

	/**
	 * Google sign in client ID
	 * ---
	 * @returns {string}
	 */
	get googleSignInClientID() {
		return this.dataFromJson?.googleSignInClientID;
	}

	/**
	 * Facebook OAuth Client ID
	 * ---
	 * @returns {string}
	 */
	get facebookOAuthClientID() {
		return this.dataFromJson?.facebookOAuthClientID;
	}

	/**
	 * Microsoft OAuth Client ID
	 * ---
	 * @returns {string}
	 */
	get microsoftOAuthClientID() {
		return this.dataFromJson?.microsoftOAuthClientID;
	}

	/**
	 * Microsoft OAuth Tenant ID
	 * ---
	 * @returns {string}
	 */
	get microsoftOAuthTenantID() {
		return this.dataFromJson?.microsoftOAuthTenantID;
	}

	/**
	 * Charging animation url
	 * ---
	 * @return {string}
	 * @see ThemeBase.chargingAnimationUrl
	 */
	get chargingAnimationUrl() {
		return this.theme.chargingAnimationUrl;
	}

	/**
	 * Cluster image url
	 * ---
	 * @return {{url: string, size: google.maps.Size}|undefined} undefined = default
	 * @see ThemeBase.clusterIcon
	 */
	get clusterIcon() {
		return this.theme.clusterIcon;
	}

	/**
	 * Cluster label color
	 * ---
	 * @return {string|undefined} undefined = default
	 * @see ThemeBase.clusterLabelColor
	 */
	get clusterLabelColor() {
		return this.theme.clusterLabelColor;
	}

	/**
	 * Android app url
	 * ---
	 * @returns {string}
	 */
	get androidAppUrl() {
		return this.manifest?.related_applications?.find(application => application.platform === "play")?.url;
	}

	/**
	 * iOS app url
	 * ---
	 * @returns {string}
	 */
	get iosAppUrl() {
		return this.manifest?.related_applications?.find(application => application.platform === "itunes")?.url;
	}

	/**
	 * Google Maps API key
	 * ---
	 * @return {string}
	 */
	get googleMapsAPIKey() {
		return this.dataFromJson?.googleMapsAPIKey ?? "AIzaSyDaKopefBL1yaqB-md2JkjF8MYepSzKZko";
	}

	/**
	 * OCPP Urls
	 * ---
	 * @returs {{
	 *  serverAddressWebSocketOcpp15: string,
	 *  serverAddressWebSocketOcpp16: string,
	 *  serverAddressSecureWebSocketOcpp16: string,
	 *  serverAddressWebSocketOcpp20: string,
	 * }}
	 */
	get ocppUrls() {
		return this.dataFromJson.ocppUrls ?? {};
	}

	/**
	 * Supported payment gates
	 * ---
	 * List of all supported payment gates
	 * @return {E_PaymentGate[]}
	 */
	get supportedPaymentGates() {
		return this.dataFromJson?.supportedPaymentGates ?? [
			E_PaymentGate.CSOB_EAPI,
			E_PaymentGate.KH_API,
			E_PaymentGate.SIMPLEPAY
		];
	}

	/**
	 * Language pack
	 * ---
	 * Language overrides
	 * @return {Object<string, Object>|undefined}
	 */
	get languagePack() {}

	constructor() {
		this.manifest = this.constructor.manifest;

		//Tests whether the environmentID and environmentName are being overridden
		this.environmentID;
		this.environmentName;

		if(!window.environments) {
			window.environments = {};
		}

		if(window.environments[this.environmentID]) {
			throw Error(`Conflicting environment ID "${this.environmentID}" of ${this.constructor.name} with ${window.environments[this.environmentID].constructor.name}`)
		}
		window.environments[this.environmentID] = this;
	}

	/**
	 * Get logo
	 * ---
	 * @param {string} attributes
	 * @return {string}
	 * @see ThemeBase.getLogo
	 */
	getLogo(attributes = '') {
		return this.theme.getLogo(attributes);
	}

	/**
	 * Is specific environment
	 * ---
	 * Returns whether the provided environment equals this environment
	 * @param {EnvBase} env
	 * @returns {boolean}
	 */
	is(env) {
		return env.environmentID == this.environmentID;
	}

	/**
	 * Is instance of an environment
	 * ---
	 * Returns whether the provided environmentClass is an instance of this class
	 * @param {EnvBase} envClass
	 * @return {boolean}
	 */
	isOf(envClass) {
		return this instanceof envClass;
	}

	/**
	 * Get icon path
	 * ---
	 * Returns URL path for an icon asset
	 * @param {number} size Icon size (width or height, since they are the same)
	 * @param {string} id EnvironmentID
	 * @return {string}
	 */
	getIconPath(size, id = this.assetsEnvironment.environmentID) {
		if(size) {
			return `/env/${id}/icons/icon-${size}.png`;
		}

		return `/env/${id}/icons/favicon.ico`;
	}

	/**
	 * Get favicon link element
	 * ---
	 * @param {string} id Environment ID
	 * @return {HTMLLinkElement}
	 */
	getFaviconLinkElement(id = this.assetsEnvironment.environmentID) {
		const favicon = document.createElement("link");
		favicon.rel = "icon";
		favicon.href = `/env/${id}/icons/favicon.ico`;

		return favicon;
	}

	/**
	 * Apply
	 * ---
	 * Apply this environment
	 */
	apply() {
		this.coreApply();
	}

	coreApply() {
		this.theme.apply();
		this.applyVariables();
		this.applyLogoToLoader();
		this.applyIconsAndMetadata();
	}

	/**
	 * Apply logo to loader
	 * ---
	 * Applies logo from get logoForLoader() to the #loaderLogo element located in index.html
	 */
	applyLogoToLoader() {
		const logo = this.logoForLoader;

		if(logo) {
			document.body.querySelector("#loaderLogo").innerHTML = logo;
		}
	}

	/**
	 * Apply variables
	 * ---
	 * Applies CSS variables to the :root
	 */
	applyVariables() {
		const vars = this.styleVariables;

		Object.keys(vars || {}).forEach(key => {
			document.documentElement.style.setProperty(key, vars[key]);
		});
	}

	/**
	 * Apply icons and metadata
	 * ---
	 * Creates and applies various icons and manifests
	 * @param {string} id Environment ID
	 */
	applyIconsAndMetadata(id = this.assetsEnvironment.environmentID) {
		const nodes = [];
		const primaryColor = this.primaryColor;

		// Title
		document.title = this.manifest.name;

		// Description
		const descriptionEl = document.createElement("meta");
		descriptionEl.content = this.manifest.description;
		nodes.push(descriptionEl);

		// Favicon
		const faviconEl = this.getFaviconLinkElement(id);
		nodes.push(faviconEl);

		// Sized favicon
		const sizedFaviconEl = this.getFaviconLinkElement(id);
		sizedFaviconEl.sizes = "16x16 32x32 64x64";
		nodes.push(sizedFaviconEl);

		// Shortcut icon
		const shortcutIconEl = this.getFaviconLinkElement(id);
		shortcutIconEl.rel = "shortcut icon";
		nodes.push(shortcutIconEl);

		// Apple
		[57, 60, 72, 76, 114, 120, 144, 152, 180].forEach(size => {
			const el = document.createElement("link");
			el.rel = "apple-touch-icon";
			el.sizes = `${size}x${size}`;
			el.href = this.getIconPath(size, id);
			nodes.push(el);
		});

		// Icons
		[16, 32, 64, 96, 160, 192].forEach(size => {
			const el = document.createElement("link");
			el.rel = "icon";
			el.sizes = `${size}x${size}`;
			el.href = this.getIconPath(size, id);
			nodes.push(el);
		});

		// Manifest
		// See https://goo.gl/OOhYW5
		const manifestEl = document.createElement("link");
		manifestEl.rel = "manifest";
		manifestEl.href = this.constructManifestUrl(id, this.manifest);
		nodes.push(manifestEl);

		// MS tile image
		const msTileImageEl = document.createElement("meta");
		msTileImageEl.name = "msapplication-TileImage";
		msTileImageEl.content = this.getIconPath(144, id);
		nodes.push(msTileImageEl);

		// MS tile color
		const msTileColorEl = document.createElement("meta");
		msTileColorEl.name = "msapplication-TileColor";
		msTileColorEl.content = primaryColor;
		nodes.push(msTileColorEl);

		// MS config
		const msConfig = msConfigTemplate({
			id,
			tileColor: this.backgroundColor,
		});
		const msConfigBlob = new Blob([msConfig], {type: 'application/xml'});

		const msConfigEl = document.createElement("meta");
		msConfigEl.content = URL.createObjectURL(msConfigBlob);
		nodes.push(msConfigEl);

		// SEO

		document.querySelector(`meta[name="description"]`)?.setAttribute("content", this.manifest.description);
		this.manifest.keywords && document.querySelector(`meta[name="keywords"]`)?.setAttribute("content", this.manifest.keywords);
		document.querySelector(`meta[property="og:title"]`)?.setAttribute("content", this.manifest.name);
		document.querySelector(`meta[property="og:description"]`)?.setAttribute("content", this.manifest.description);
		document.querySelector(`meta[property="og:image"]`)?.setAttribute("content", `${window.location.origin}/emv/${this.assetsEnvironment.environmentID}/assets/images/share-image.png`);
		document.querySelector(`meta[property="og:url"]`)?.setAttribute("content", window.location.origin);
		document.querySelector(`meta[name="twitter:title"]`)?.setAttribute("content", this.manifest.name);
		document.querySelector(`meta[name="twitter:description"]`)?.setAttribute("content", this.manifest.description);
		document.querySelector(`meta[name="twitter:image"]`)?.setAttribute("content", `${window.location.origin}/emv/${this.assetsEnvironment.environmentID}/assets/images/share-image.png`);

		// Theme color
		const themeColorEl = document.createElement("meta");
		themeColorEl.name = "theme-color";
		themeColorEl.content = primaryColor;
		nodes.push(themeColorEl);

		document.head.append(...nodes);
	}

	/**
	 * Construct manifest for URL
	 * ---
	 * Returns an URL for the dynamically created manifest.json
	 * @param {string} id environmentID
	 * @param {manifest} manifest Manifest object
	 * @return {string} Url
	 */
	constructManifestUrl(id, manifest) {
		//The manifest json carries no colors of its own - they belong to the theme
		manifest.theme_color = this.primaryColor;
		manifest.background_color = this.backgroundColor;

		manifest.icons = manifest.icons?.map(icon => {
			if(icon.src.startsWith("~/")) {
				return {
					...icon,
					src: icon.src.replace("~/", `${window.location.origin}/env/${id}/`),
				}
			}

			if(icon.src.startsWith("/env")) {
				return {
					...icon,
					src: icon.src.replace("/env", `${window.location.origin}/env/`),
				}
			}

			return icon;
		}) ?? [];

		/**
		 * Related applications
		 * @see PWARelatedApplication
		 */
		if(this.androidAppUrl || this.iosAppUrl) {
			manifest.related_applications = manifest.related_applications ?? [
				this.androidAppUrl && {
					platform: "play",
					url: this.androidAppUrl,
					id: new URL(this.androidAppUrl).searchParams.get("id"),
				},
				this.iosAppUrl && {
					platform: "itunes",
					url: this.iosAppUrl,
				}
			].filter(v => !!v);
		}

		const stringManifest = JSON.stringify(manifest);
		const manifestBlob = new Blob([stringManifest], {type: 'application/json'});

		return URL.createObjectURL(manifestBlob);
	}
}

// noinspection JSClosureCompilerSyntax
export const SimpleEnvBase = (manifest) => class SimpleEnvBase extends EnvBase {
	static manifest = manifest;
};
